import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { Component, ElementRef, TemplateRef, inject, input, viewChild } from "@angular/core";

import { environment } from "src/environments/environment";
import { AuthService, Service, TourServicesCodes } from "src/app/core";

import { ButtonComponent } from "../button/button.component";
import { LocalizedNamePipe } from "../../pipes";
import { ServiceStatusDialogComponent } from "src/app/features/book-service/components/service-status-dialog/service-status-dialog.component";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "app-service-card",
  standalone: true,
  imports: [TranslateModule, MatDialogModule, ButtonComponent, LocalizedNamePipe],
  templateUrl: "./service-card.component.html",
  styleUrl: "./service-card.component.scss",
})
export class ServiceCardComponent {
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private toaster = inject(ToastrService);
  private authService = inject(AuthService);
  private translateService = inject(TranslateService);

  get isValidForLoggedUsersOnly() {
    return [
      TourServicesCodes.SchoolUniversityVisits,
      TourServicesCodes.TourismCompanies,
      TourServicesCodes.OfficialInstitutions,
      TourServicesCodes.GuidedTours,
      TourServicesCodes.FriendsOfOmanMuseum,
    ].includes(this.service().code as TourServicesCodes);
  }

  readonly isAdmin = this.authService.isAdmin;

  readonly service = input.required<Service>();
  readonly titleClass = input.required<string>();
  readonly imageDisplaying = input(true);
  readonly requestStatus = input.required<number>();

  readonly loginModal = viewChild.required<TemplateRef<any>>("loginModal");
  readonly serviceCard = viewChild.required<ElementRef<HTMLDivElement>>("serviceCard");

  openModal() {
    if (localStorage.getItem(environment.tokenPropertyName)) {
      this.checkSubmittedRequests();
    } else {
      this.dialog.open(this.loginModal(), {
        panelClass: ["small", "h-max-content", "p-0"],
        autoFocus: false,
      });
    }
  }

  openServiceStatusDialog(requestStatus: number, serviceCode: string) {
    this.dialog.open(ServiceStatusDialogComponent, {
      data: {
        id: requestStatus,
        serviceCode: serviceCode,
      },
      panelClass: ["small", "h-max-content", "p-0"],
      autoFocus: false,
    });
  }

  checkSubmittedRequests() {
    const service = this.service();
    const requestStatus = this.requestStatus();
    const eligibleServices = [TourServicesCodes.TourismCompanies, TourServicesCodes.FriendsOfOmanMuseum];

    if (requestStatus !== 1 && eligibleServices.includes(service.code as TourServicesCodes)) {
      this.openServiceStatusDialog(requestStatus, service.code);
    } else if (service.code === TourServicesCodes.FriendsOfOmanMuseum && requestStatus === 1) {
      this.toaster.info(this.translateService.instant("REQUEST_MESSAGES.ONE_FREE_COMPANION"));
      this.router.navigate([service.serviceRoute]);
    } else {
      this.router.navigate([service.serviceRoute]);
    }
  }

  loginAsGuest() {
    this.authService.loginAsGuest().subscribe(() => {
      this.dialog.closeAll();
      this.router.navigate([this.service().serviceRoute]);
    });
  }

  login() {
    this.dialog.closeAll();
    this.router.navigate(["/auth/login"], { queryParams: { redirect: this.service().serviceRoute } });
  }

  ngAfterContentInit(): void {
    //Called after ngOnInit when the component's or directive's content has been initialized.
    //Add 'implements AfterContentInit' to the class.

    if (this.imageDisplaying()) {
      this.serviceCard().nativeElement.style.setProperty(
        "background-image",
        `url(../../../../assets/images/services-images/${this.service().code}.png)`,
      );
    }
  }
}
